import type { LucideIcon } from 'lucide-react';

import { usePopoverPlacement, type PopoverAnchor } from '../../hooks';
import { cn } from '../../lib/utils';

export interface RowActionItem {
  id: string;
  /** Lucide icon component rendered before the label. */
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}

export interface RowActionPopoverProps {
  /** Header text — typically the hovered row's label. */
  heading: string;
  actions: readonly RowActionItem[];
  /**
   * The hovered row's label. The popover opens below it, left-aligned to the
   * label's start so the row's own counts stay visible, and flips above when it
   * would cross the widget card's bottom (`placePopover`).
   */
  anchor: PopoverAnchor;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  className?: string;
}

export function RowActionPopover({
  heading,
  actions,
  anchor,
  onMouseEnter,
  onMouseLeave,
  className,
}: RowActionPopoverProps) {
  const { ref, style } = usePopoverPlacement(anchor);

  return (
    <div
      ref={ref}
      data-testid="row-action-popover"
      role="menu"
      aria-label={heading}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        // Figma `5:20399`: 217 wide, 20px padding, one inset divider under the
        // heading, action rows on a 28px pitch with 12px glyphs.
        'flex min-w-[217px] flex-col overflow-hidden rounded-lg border border-grey-200 bg-white p-5 shadow-lg',
        className
      )}
    >
      <p className="text-[13px] font-medium leading-[14px] text-dawn-black">{heading}</p>
      <div className="my-4 h-px bg-grey-100" aria-hidden="true" />
      <div className="flex flex-col gap-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              onClick={action.onClick}
              // Negative margin lets the hover band span the full popover while
              // the label keeps its 20px inset.
              // `leading-3` pins the row to Figma's 12px; `-my-1 py-1` grows the hover
              // band to 20px without changing the 28px pitch it sits on.
              className="-mx-5 -my-1 flex items-center gap-1 px-5 py-1 text-left text-[12px] leading-3 text-dawn-black hover:bg-grey-50"
            >
              <Icon className="h-3 w-3 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              {action.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

RowActionPopover.displayName = 'RowActionPopover';
